import { useEffect, useMemo, useState } from 'react';
import { Tooltip, Modal, Box } from '@mui/material';

import { DataTable, EditIcon, SyncIcon, TrashIcon, type TableColumn } from '@/src/ui';
import type { ExamAssignmentBasicDTO, ExamAssignmentUpdateDTO } from '../models/assignmentDTOs';
import { apiRequest } from '@/src/services';
import { ReportHeaderStyle, ReportCellStyle, utcToLocal, formatOptions } from '@/src/utils';

interface ExamAssignmentsTableProps {
    data: Array<ExamAssignmentBasicDTO>;
    setShowCreateEditModal: (val: boolean) => void;
    showSyncButton: boolean;
    setShowSyncModal: (val: boolean) => void;
    setShowDeleteModal: (val: boolean) => void;
    modalMode: 'create' | 'edit' | 'view' | null;
    setModalMode: (val: 'create' | 'edit' | 'view' | null) => void;
    setSelectedExamAssignment: (val: ExamAssignmentUpdateDTO | null) => void;
    searchString: string;
}

export default function ExamAssignmentsTable(props: ExamAssignmentsTableProps) {
    const {
        data,
        setShowCreateEditModal,
        showSyncButton,
        setShowSyncModal,
        setShowDeleteModal,
        modalMode,
        setModalMode,
        setSelectedExamAssignment,
        searchString,
    } = props;

    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [editableIds, setEditableIds] = useState<Set<number>>(new Set());

    const handleCloseError = () => setErrorMessage(null);

    useEffect(() => {
        const fetchEditableIds = async () => {
            try {
                const res = await apiRequest<number[]>(
                    'GET',
                    'assessments/examAssignments/isEditable'
                );
                if (Array.isArray(res.data)) {
                    setEditableIds(new Set(res.data));
                } else {
                    console.error('Unexpected response format for editable IDs:', res.data);
                }
            } catch (err) {
                console.error('Failed to fetch editable IDs:', err);
                setErrorMessage('Failed to fetch status for exam assignments.');
            }
        };
        fetchEditableIds();
    }, []);

    const filteredData = useMemo(() => {
        return data.filter((ea) => {
            const search = searchString.trim().toLowerCase();
            if (!search) return true;
            return (
                `${ea.examName}`.trim().toLowerCase().includes(search) ||
                `${ea.teacherName}`.trim().toLowerCase().includes(search)
            );
        });
    }, [data, searchString]);

    const columns = useMemo<TableColumn[]>(() => {
        return [
            {
                dataKey: 'examName',
                header: 'Name',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
            },
            {
                dataKey: 'examTypeMasterName',
                header: 'Type',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
            },
            {
                dataKey: 'duration',
                header: 'Duration',
                align: 'left',
                allowSort: true,
                emptyValue: '∞',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
            },
            {
                dataKey: 'numberOfAttemptsAllowed',
                header: 'Attempts',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
            },
            {
                dataKey: 'dateRange',
                header: 'Date Range',
                align: 'left',
                width: 100,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row) => {
                    const from = row.fromDate
                        ? utcToLocal(row.fromDate)?.toLocaleString(undefined, formatOptions)
                        : '--';
                    const to = row.toDate
                        ? utcToLocal(row.toDate)?.toLocaleString(undefined, formatOptions)
                        : '--';

                    return `${from} - ${to}`;
                },
            },
            {
                dataKey: 'teacherName',
                header: 'Teacher Name',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
            },
            {
                dataKey: 'actions',
                header: 'Actions',
                align: 'left',
                width: 120,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: ExamAssignmentUpdateDTO) => {
                    const isEditable = editableIds.has(row.id);

                    return (
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <Tooltip title={isEditable ? 'Edit' : 'View (Editing Disabled)'} arrow>
                                <button
                                    className="icon-button"
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => {
                                        if (!modalMode) {
                                            setSelectedExamAssignment(row);
                                            setShowCreateEditModal(true);
                                            if (isEditable) {
                                                setModalMode('edit');
                                            } else {
                                                setModalMode('view');
                                            }
                                        }
                                    }}
                                >
                                    {isEditable ? (
                                        <EditIcon style={{ width: '28px', height: '28px' }} />
                                    ) : (
                                        <EditIcon
                                            color="red"
                                            style={{ width: '28px', height: '28px' }}
                                        />
                                    )}
                                </button>
                            </Tooltip>
                            {!isEditable && showSyncButton && (
                                <Tooltip title={'Sync Attempts'} arrow>
                                    <button
                                        className="icon-button"
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                        }}
                                        onClick={() => {
                                            if (!modalMode) {
                                                setSelectedExamAssignment(row);
                                                setShowSyncModal(true);
                                            }
                                        }}
                                    >
                                        <SyncIcon style={{ width: '28px', height: '28px' }} />
                                    </button>
                                </Tooltip>
                            )}
                            <Tooltip title="Delete" arrow>
                                <button
                                    className="icon-button"
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: !modalMode ? 'pointer' : 'not-allowed',
                                        opacity: !modalMode ? 1 : 0.6,
                                    }}
                                    disabled={!!modalMode}
                                    onClick={() => {
                                        if (!modalMode) {
                                            setSelectedExamAssignment(row);
                                            setShowDeleteModal(true);
                                        }
                                    }}
                                >
                                    <TrashIcon style={{ width: '28px', height: '28px' }} />
                                </button>
                            </Tooltip>
                        </div>
                    );
                },
            },
        ];
    }, [
        setSelectedExamAssignment,
        modalMode,
        setModalMode,
        setShowCreateEditModal,
        setShowDeleteModal,
        editableIds,
        showSyncButton,
        setShowSyncModal,
    ]);

    return (
        <>
            <Modal open={!!errorMessage} onClose={handleCloseError}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        boxShadow: 24,
                        p: 3,
                        minWidth: 300,
                    }}
                >
                    <p style={{ color: 'red', margin: 0 }}>Error: {errorMessage}</p>
                    <button onClick={handleCloseError} style={{ marginTop: 16 }}>
                        Close
                    </button>
                </Box>
            </Modal>

            <DataTable
                data={filteredData}
                columns={columns}
                tableName="Assessments"
                alternatingRowColor
                defaultSortColumn="examName"
                defaultSortDirection="asc"
            />
        </>
    );
}
